import { Environment, ENVIRONMENT_TOKEN, StoreWrapperInterface, STORE_WRAPPER_TOKEN, UserInterface } from '@actassa/api';
import { ComplexResponse, paramReplacer } from '@actassa/shared';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { isArray, isEmpty, isEqual, isString } from 'lodash-es';
import moment from 'moment';
import { merge, NEVER, Observable, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    switchMap,
    take,
    tap,
    concatMap,
    withLatestFrom,
    filter,
    throttleTime,
    map,
} from 'rxjs/operators';
import { uuidv7 as uuid } from 'uuidv7';

import { DeleteDataCollection } from '../+state/actions/delete-data-collection';
import { GetInspectionsRaw } from '../+state/actions/get-inspections-raw';
import { LoadDataCollections } from '../+state/actions/load-data-collections';
import { LoadDataCollectionsForced } from '../+state/actions/load-data-collections-forced';
import { LoadDataCollectionsSuccess } from '../+state/actions/load-data-collections-success';
import { SaveDataCollection } from '../+state/actions/save-data-collection';
import { UpdateDataCollection } from '../+state/actions/update-data-collection';
import { THROTTLE_TIMEOUT } from '../constants/constants';
import { RoutesDictionary } from '../dictionaries/routes.dictionary';
import { checkDependency } from '../helpers/comparator-functions.helper';
import { initSubstitutionStream } from '../helpers/substitution-stream.helper';
import { DataCollectionInterface } from '../interfaces/data-collection.interface';
import { TemplateInterface } from '../interfaces/template.interface';
import { LogicOperator, UserAction } from '../interfaces/user-action.interface';

import { LoaderService } from './loader.service';
import { TemplateControl } from './template-control';
import { UserActionService } from './user-action.service';

const DEFAULT_LOAD_DAYS = 30;

@Injectable({
    providedIn: 'root',
})
export class InspectionsService {
    constructor(
        @Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment,
        @Inject(STORE_WRAPPER_TOKEN) private storeWrapper: StoreWrapperInterface,
        private readonly actions$: Actions,
        private readonly http: HttpClient,
        private readonly userActionService: UserActionService,
        private readonly loaderService: LoaderService,
    ) { }

    public init$(): Observable<unknown> {
        return merge(
            this.actions$.pipe(
                ofActionDispatched(GetInspectionsRaw),
                switchMap(() => this.load$()),
                catchError((error) => {
                    this.storeWrapper.showToast(error.message);

                    return NEVER;
                }),
            ),
            this.actions$.pipe(
                ofActionDispatched(LoadDataCollections),
                throttleTime(THROTTLE_TIMEOUT),
                tap(() => this.loaderService.loadingStart()),
                switchMap(() => this.load$()),
                tap(() => this.loaderService.loadingEnd()),
                catchError((error) => {
                    this.storeWrapper.showToast(error.message);
                    this.loaderService.loadingEnd();

                    return NEVER;
                }),
            ),
            this.actions$.pipe(
                ofActionDispatched(LoadDataCollectionsForced),
                tap(() => this.loaderService.loadingStart()),
                switchMap(() => this.load$()),
                tap(() => this.loaderService.loadingEnd()),
                catchError((error) => {
                    this.storeWrapper.showToast(error.message);
                    this.loaderService.loadingEnd();

                    return NEVER;
                }),
            ),
            this.actions$.pipe(
                ofActionDispatched(SaveDataCollection),
                distinctUntilChanged(isEqual),
                withLatestFrom(this.storeWrapper.isAuthenticated$),
                filter(([, isAuthenticated]: [SaveDataCollection, boolean]) => isAuthenticated),
                concatMap(([{ dataCollection }]: [SaveDataCollection, boolean]) => this.create$(dataCollection)),
                catchError((error) => {
                    this.storeWrapper.showToast(error.message);

                    return NEVER;
                }),
            ),
            this.actions$.pipe(
                ofActionDispatched(UpdateDataCollection),
                distinctUntilChanged(isEqual),
                withLatestFrom(this.storeWrapper.isAuthenticated$),
                filter(([, isAuthenticated]: [UpdateDataCollection, boolean]) => isAuthenticated),
                concatMap(([{ dataCollection }]: [UpdateDataCollection, boolean]) => this.update$(dataCollection)),
                catchError((error) => {
                    this.storeWrapper.showToast(error.message);

                    return NEVER;
                }),
            ),
            this.actions$
                .pipe(
                    ofActionDispatched(DeleteDataCollection),
                    map(({ dataCollection }: DeleteDataCollection) => dataCollection.id),
                    distinctUntilChanged(),
                    withLatestFrom(this.storeWrapper.isAuthenticated$),
                    filter(([, isAuthenticated]: [string, boolean]) => isAuthenticated),
                    concatMap(([id]: [string, boolean]) => this.delete$(id)),
                    catchError((error) => {
                        this.storeWrapper.showToast(error.message);

                        return NEVER;
                    }),
                ),
        );
    }

    public batchDataCollectionCreate$(dataCollection: DataCollectionInterface, isFinished: boolean): Observable<unknown> {
        const form = this.createFormGroup(dataCollection);
        const preparedDataCollection = this.prepare(dataCollection, form, isFinished);

        this.saveInspection(preparedDataCollection);

        if (preparedDataCollection.isFinished) {
            this.handlePostFinishActions(preparedDataCollection);

            return of(null);
        }

        return this.storeWrapper.baseUrl$
            .pipe(
                take(1),
                tap((baseUrl) => {
                    const localUrl = RoutesDictionary.DATA_COLLECTION_INSPECTION;

                    this.gotoInspection(`${baseUrl}/${localUrl}/${preparedDataCollection.id}`);
                }),
            );
    }

    public mapTemplateInspection(template: TemplateInterface, user: UserInterface): DataCollectionInterface {
        const currentMoment = moment().utc();
        const currentTimestamp = currentMoment.valueOf();
        const currentDateTime = currentMoment.toISOString();

        return {
            ...template,
            appId: this.environment.appId,
            createdAt: currentTimestamp,
            createdAtFormatted: currentDateTime,
            modifiedAt: currentTimestamp,
            modifiedAtFormatted: currentDateTime,
            id: uuid(),
            isFinished: false,
            isDeleted: false,
            mailBodyHtml: paramReplacer(template.mailBodyHtml, user),
            mailBodyText: paramReplacer(template.mailBodyText, user),
            mailSubject: paramReplacer(template.mailSubject, user),
            outputName: paramReplacer(template.outputName, user),
            questions: this.questionsReplacer(template.questions, user),
            taskConfig: this.questionsReplacer(template.taskConfig, user),
            templateId: template.id,
            userId: user.id,
            uuid: user.uuid,
        };
    }

    public handlePostFinishActions(inspection: DataCollectionInterface): void {
        const { postFinishActions, questions } = inspection;

        if (isArray(postFinishActions)) {
            postFinishActions.forEach((action) => {
                if (isEmpty(action?.dependencies)) {
                    this.userActionService.handle(action);

                    return;
                }

                const dependencyTopLevelLogic = this.getDependencyTopLevelLogic(action);
                const initialValue = this.getInitialValueOfCondition(action);
                const condition = action.dependencies.reduce((accumulator, dependency) => {
                    const affectedQuestion = questions.find((question) => question.key === dependency.key);

                    if (!affectedQuestion) {
                        return accumulator;
                    }

                    if (dependencyTopLevelLogic === LogicOperator.OR) {
                        return accumulator || checkDependency(dependency.value, affectedQuestion.value);
                    }

                    return accumulator && checkDependency(dependency.value, affectedQuestion.value);
                }, initialValue);

                if (condition) {
                    this.userActionService.handle(action);
                }
            });
        }
    }

    public createFormGroup({ questions }: DataCollectionInterface): FormGroup {
        const group = questions.reduce((accum, question) => {
            const { key, required, value, notes, media, tasks } = question;

            if (!isEmpty(notes)) {
                accum[notes.key] = new FormControl(notes.value || '');
            }

            if (!isEmpty(media)) {
                accum[media.key] = new FormControl(media.value || []);
            }

            if (!isEmpty(tasks)) {
                accum[tasks.key] = new FormControl(tasks.value || []);
            }

            accum[key] = required ? new FormControl(value || '', Validators.required) : new FormControl(value || '');

            return accum;
        }, {});

        return new FormGroup(group, { updateOn: 'blur' });
    }

    public prepare(
        inspection: DataCollectionInterface,
        form: FormGroup,
        isFinished?: boolean,
        isDeleted?: boolean,
    ): DataCollectionInterface {
        const formValue = { ...form.value };
        const questions = inspection.questions.map((question) => {
            const { media, notes, buttons = [], tasks } = question;
            let value = formValue[question.key];

            if (!value && question.emptyValueTemplate && isFinished) {
                value = initSubstitutionStream(question.emptyValueTemplate, formValue, inspection.questions);
            }

            return {
                ...question,
                value,
                notes: notes ? { key: notes.key, value: formValue[notes.key] } : undefined,
                media: media ? { key: media.key, value: formValue[media.key] } : undefined,
                tasks: tasks ? { key: tasks.key, value: formValue[tasks.key] } : undefined,
                buttons: buttons?.length
                    ? buttons.map((button) => {
                        const { addons = [] } = button;

                        return { ...button, addons: addons?.map((addon) => ({ ...addon, value: formValue[addon.key] })) };
                    })
                    : undefined,
            };
        });

        const currentMoment = moment().utc();
        const currentTimestamp = currentMoment.valueOf();
        const currentDateTime = currentMoment.toISOString();

        return {
            ...inspection,
            questions: [...questions],
            isFinished: inspection.isFinished ? true : isFinished,
            isDeleted,
            modifiedAt: currentTimestamp,
            modifiedAtFormatted: currentDateTime,
            deletedAt: isDeleted ? currentTimestamp : undefined,
            deletedAtFormatted: isDeleted ? currentDateTime : undefined,
        };
    }

    public rawLoad$(): Observable<ComplexResponse<Array<DataCollectionInterface>>> {
        return this.storeWrapper.getMenuItemProperty$('COMMON_CONFIG', 'dataCollectionMaximumStorageDays')
            .pipe(
                map((dataCollectionMaximumStorageDays = DEFAULT_LOAD_DAYS) =>
                    `${this.environment.apiURL}/inspections/days/${dataCollectionMaximumStorageDays}`),
                switchMap((url: string) => this.http.get<ComplexResponse<Array<DataCollectionInterface>>>(url)),
                take(1),
            );
    }

    private load$(): Observable<ComplexResponse<Array<DataCollectionInterface>>> {
        return this.rawLoad$()
            .pipe(
                tap((response: ComplexResponse<Array<DataCollectionInterface>>) => {
                    this.responseMessageHandler(response);
                    this.responseDataHandler(response);
                }),
            );
    }

    private create$(inspection: DataCollectionInterface): Observable<ComplexResponse<Array<DataCollectionInterface>>> {
        const url = `${this.environment.apiURL}/inspections`;

        return this.http.patch<ComplexResponse<Array<DataCollectionInterface>>>(url, inspection).pipe(
            take(1),
            tap((response) => this.responseMessageHandler(response)),
        );
    }

    private update$(inspection: Partial<DataCollectionInterface>): Observable<ComplexResponse<Array<DataCollectionInterface>>> {
        const url = `${this.environment.apiURL}/v1/data-collection/${inspection.id}`;

        return this.http.patch<ComplexResponse<Array<DataCollectionInterface>>>(url, inspection).pipe(
            take(1),
        );
    }

    private delete$(id: string): Observable<void> {
        const url = `${this.environment.apiURL}/inspections/${id}`;

        return this.http.delete<void>(url)
            .pipe(
                take(1),
            );
    }

    private responseMessageHandler(response: ComplexResponse<Array<DataCollectionInterface>>): void {
        if (!response) {
            this.storeWrapper.showToast('Data request error. Please, check internet connection.');

            return;
        }

        const { message } = response;

        this.storeWrapper.showToast(message);
    }

    private responseDataHandler(response: ComplexResponse<Array<DataCollectionInterface>>): void {
        if (!response) {
            return;
        }

        const { status, data } = response;

        if (status === 'ok' && data) {
            this.loadInspections(data);
        }
    }

    private questionsReplacer(questions: Array<TemplateControl<any>>, user: UserInterface): Array<TemplateControl<any>> {
        return questions?.map((question) => ({
            ...question,
            label: paramReplacer(question.label, user),
            value: paramReplacer(question.value, user),
            buttons: question.buttons?.length
                ? question.buttons.map((button) => ({
                    ...button,
                    label: paramReplacer(button.label, user),
                    value: paramReplacer(button.value, user),
                    addons: button.addons?.length
                        ? button.addons.map((addon) => ({
                            ...addon,
                            label: paramReplacer(addon.label, user),
                            value: paramReplacer(addon.value, user),
                        }))
                        : undefined,
                }))
                : undefined,
        }));
    }

    private getDependencyTopLevelLogic({ dependencyTopLevelLogic }: UserAction): LogicOperator {
        if (isString(dependencyTopLevelLogic) && dependencyTopLevelLogic.toUpperCase() === LogicOperator.OR) {
            return LogicOperator.OR;
        }

        return LogicOperator.AND;
    }

    private getInitialValueOfCondition({ dependencyTopLevelLogic }: UserAction): boolean {
        if (isString(dependencyTopLevelLogic) && dependencyTopLevelLogic.toUpperCase() === LogicOperator.OR) {
            return false;
        }

        return true;
    }

    @Dispatch()
    private loadInspections(dataCollections: Array<DataCollectionInterface>): LoadDataCollectionsSuccess {
        return new LoadDataCollectionsSuccess(dataCollections);
    }

    @Dispatch()
    public saveInspection(dataCollection: DataCollectionInterface): SaveDataCollection {
        return new SaveDataCollection(dataCollection);
    }

    @Dispatch()
    private gotoInspection(url: string): Navigate {
        return new Navigate([url]);
    }
}
